import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { connect, useDispatch } from 'react-redux';
import { injectIntl } from 'react-intl';
import { CardHeader } from '../../components/CardHeader';
import { TextField } from '../../components/TextFieldInput';
import * as auth from '../../modules/Auth/_redux/authRedux';
import { Checkbox, NativeSelect } from '@material-ui/core';
import { Button } from '../../components/Button';
import { ErrorMessage } from '../../components/ErrorMessage';
import { toAbsoluteUrl } from '../../../_metronic/_helpers';
import { BootstrapInput } from '../../components/SelectInput';
import { Body, Cell, Head, Row, Table } from '../../components/Table';
import { DeleteButton } from '../../components/DeleteButton';
import ImageCrop from '../../components/ImageCrop';
import SVG from 'react-inlinesvg';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { shallowEqual, useSelector } from 'react-redux';
import { ImagePlaceholder } from '../../components/ImagePlaceholder';
import { Loading } from '../../components/Loading';

function getSchema(intl) {
  return Yup.object().shape({
    email: Yup.string()
      .email(intl.formatMessage({ id: 'GENERAL.WRONG_EMAIL' }))
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })),
    firstName: Yup.string().required(
      intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })
    ),
    lastName: Yup.string().required(
      intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })
    ),
    department: Yup.string().required(
      intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })
    ),
    jobTitle: Yup.string().required(
      intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })
    ),
    enabled: Yup.bool().required(
      intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })
    ),
    roles: Yup.array()
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' }))
      .min(1),
  });
}

const initialValues = {
  email: '',
  firstName: '',
  lastName: '',
  department: '',
  jobTitle: '',
  picture: '',
  enabled: true,
  businessEntityAdmin: false,
  roles: [],
};

const LABEL_SIZE = 95;

const roles = [
  { id: 1, description: 'Admin' },
  { id: 2, description: 'Project Manager' },
];

function BusinessEntitiesUsersCreate(props) {
  const loggedUser = useSelector(({ auth }) => auth.user, shallowEqual);
  const dispatch = useDispatch();
  let { id, userId } = useParams();
  const [user, setUser] = useState(initialValues);
  const [entity, setEntity] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [picture, setPicture] = useState({});
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  if (userId === 'new') userId = null;

  useEffect(() => {
    async function getUser() {
      try {
        setIsLoading(true);
        const [entityReq, projectReq] = await Promise.all([
          axios.get(`/business-entities/${id}`),
          axios.get(`/business-entities/${id}/projects`),
        ]);
        if (entityReq.data) setEntity(entityReq.data);
        if (projectReq.data) {
          setProjects(projectReq.data);
          setSelectedProject(projectReq.data[0].id);
          setSelectedRole(roles[0].description);
        }
      } finally {
        setIsLoading(false);
      }
      if (userId) {
        try {
          setIsLoading(true);
          const { data } = await axios.get(
            `/business-entities/${id}/users/${userId}`
          );
          if (data) {
            data.picture = data.picture
              ? import.meta.env.VITE_IMAGE_BUCKET + 'users/' + data.picture
              : null;
            setUser(data);
          }
        } finally {
          setIsLoading(false);
        }
      }
    }
    getUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const formik = useFormik({
    initialValues: user,
    enableReinitialize: true,
    validationSchema: getSchema(props.intl),
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setSubmitting(true);
      delete values.id;
      delete values.cognitoArn;
      delete values.businessEntityId;
      try {
        if (!userId) await axios.post(`/business-entities/${id}/users`, values);
        else
          await axios.put(`/business-entities/${id}/users/${userId}`, values);
      } finally {
        setSubmitting(false);
      }
      if (values.email === loggedUser.email && +id === loggedUser.entityId) {
        const newValues = {
          ...loggedUser,
          jobTitle: values.jobTitle,
          department: values.department,
          fullname: `${values.firstName} ${values.lastName}`,
          picture: values.picture
            ? import.meta.env.VITE_IMAGE_BUCKET +
              'users/' +
              loggedUser.arn +
              `.jpeg?t=${Date.now()}`
            : null,
        };
        dispatch(props.setUser(newValues));
      }
      props.history.goBack();
    },
  });

  function onCancel(evt) {
    evt.preventDefault();
    evt.stopPropagation();
    props.history.goBack();
  }

  function uploadPhoto(e) {
    const file = e.target.files[0];
    setPicture({
      fileUrl: URL.createObjectURL(file),
      file,
      filename: file.name,
    });
  }

  function addPermission() {
    const currentRoles = formik.values.roles;
    if (currentRoles.find(x => x.projectId === +selectedProject)) return;
    const values = currentRoles.concat({
      projectId: +selectedProject,
      role: selectedRole,
    });
    formik.setFieldValue('roles', values);
  }

  function removePermission(id) {
    const values = formik.values.roles.filter(x => x.projectId !== id);
    formik.setFieldValue('roles', values);
  }

  return (
    <div className="card">
      <CardHeader
        title={props.intl.formatMessage(
          {
            id: userId
              ? 'ADMINISTRATION.BUSINESS.EDIT.USER_DATA.TITLE'
              : 'ADMINISTRATION.BUSINESS.CREATE.USER.TITLE',
          },
          { name: entity.name }
        )}
        subtitle={props.intl.formatMessage({
          id: 'ADMINISTRATION.BUSINESS.CREATE.USER.SUBTITLE',
        })}
      />
      <div className="card-body row">
        <Loading isLoading={isLoading} />
        <div className="col-sm-12 col-md-12 col-lg-6 col-xl-6">
          <div
            className="image-input image-input-outline mb-1"
            id="kt_profile_avatar"
            style={{
              backgroundImage: `url(${toAbsoluteUrl('/media/users/blank.png')}`,
            }}
          >
            {formik.values.picture ? (
              <img
                className="image-input-wrapper"
                alt="user"
                src={formik.values.picture}
              />
            ) : (
              <ImagePlaceholder />
            )}
            <label
              className="btn btn-xs btn-icon btn-circle btn-white btn-hover-text-primary btn-shadow"
              data-action="change"
              data-toggle="tooltip"
              title=""
              data-original-title="Change avatar"
            >
              <i className="fa fa-pen icon-sm text-muted"></i>
              <input
                type="file"
                id="user-image-account"
                accept=".png,.jpg,.jpeg"
                onChange={uploadPhoto}
              />
              <input type="hidden" name="profile_avatar_remove" />
            </label>
            <span
              className="btn btn-xs btn-icon btn-circle btn-white btn-hover-text-primary btn-shadow"
              data-action="cancel"
              data-toggle="tooltip"
              title=""
              data-original-title="Cancel avatar"
            >
              <i className="ki ki-bold-close icon-xs text-muted"></i>
            </span>
            <span
              className="btn btn-xs btn-icon btn-circle btn-white btn-hover-text-primary btn-shadow"
              data-action="remove"
              data-toggle="tooltip"
              title=""
              onClick={() => {
                formik.setFieldValue('picture', null);
                document.getElementById('user-image-account').value = null;
              }}
              data-original-title="Remove avatar"
            >
              <i className="ki ki-bold-close icon-xs text-muted"></i>
            </span>
          </div>
          <div className="d-block mb-5">
            <span className="text-muted mt-5">
              {props.intl.formatMessage({ id: 'FILE_TYPES' })}
            </span>
          </div>
          <form onSubmit={formik.handleSubmit}>
            <TextField
              field="email"
              inputSize="100%"
              disabled={userId ? true : false}
              labelMaxSize={LABEL_SIZE}
              labelMinSize={LABEL_SIZE}
              formik={formik}
              type="email"
              label={props.intl.formatMessage({ id: 'AUTH.INPUT.EMAIL' })}
            />
            <TextField
              field="firstName"
              inputSize="100%"
              labelMaxSize={LABEL_SIZE}
              labelMinSize={LABEL_SIZE}
              formik={formik}
              label={props.intl.formatMessage({ id: 'FIRST_NAME' })}
            />
            <TextField
              field="lastName"
              inputSize="100%"
              labelMaxSize={LABEL_SIZE}
              labelMinSize={LABEL_SIZE}
              formik={formik}
              label={props.intl.formatMessage({ id: 'LAST_NAME' })}
            />
            <TextField
              field="department"
              inputSize="100%"
              labelMaxSize={LABEL_SIZE}
              labelMinSize={LABEL_SIZE}
              formik={formik}
              label={props.intl.formatMessage({ id: 'DEPARTMENT' })}
            />
            <TextField
              field="jobTitle"
              inputSize="100%"
              labelMaxSize={LABEL_SIZE}
              labelMinSize={LABEL_SIZE}
              formik={formik}
              label={props.intl.formatMessage({ id: 'JOB_TITLE' })}
            />
            <div className="form-group fv-plugins-icon-container">
              <Checkbox
                className="pl-0"
                {...formik.getFieldProps('enabled')}
                checked={formik.values.enabled}
              />
              <label>{props.intl.formatMessage({ id: 'ENABLED' })}</label>
            </div>
            <Button
              classes="px-15"
              formik={formik}
              type="submit"
              onClick={formik.handleSubmit}
              title={props.intl.formatMessage({
                id: userId ? 'UPDATE' : 'CREATE',
              })}
            />
            {!userId ? (
              <Button
                classes="px-15 ml-10"
                style={{
                  backgroundColor: 'white',
                  color: '#58595B',
                  borderColor: '#58595B',
                }}
                onClick={onCancel}
                title={props.intl.formatMessage({ id: 'SKIP' })}
              />
            ) : null}
          </form>
        </div>
        <div className="col-sm-12 col-md-12 col-lg-6 col-xl-6">
          <div style={{ height: 139 }} className="mb-6"></div>
          <div className="d-flex align-items-center">
            <div className="col-sm-5 col-md-5 col-lg-5 col-xl-5 px-0">
              <div className={`form-group fv-plugins-icon-container d-block`}>
                <label style={{ minWidth: 50 }}>
                  {props.intl.formatMessage({ id: 'PROJECTS' })}
                </label>
                <NativeSelect
                  className="form-select"
                  defaultValue={selectedProject?.id || ''}
                  style={{ width: '100%', height: 40.5 }}
                  input={<BootstrapInput />}
                  onChange={({ target: { value } }) =>
                    setSelectedProject(value)
                  }
                >
                  {projects.map(opt => (
                    <option value={opt.id} key={opt.id}>
                      {' '}
                      {opt.name}{' '}
                    </option>
                  ))}
                </NativeSelect>
              </div>
            </div>
            <div className="col-sm-5 col-md-5 col-lg-5 col-xl-5 pr-0">
              <div className={`form-group fv-plugins-icon-container d-block`}>
                <label style={{ minWidth: 50 }}>
                  {props.intl.formatMessage({ id: 'ROLE' })}
                </label>
                <NativeSelect
                  className="form-select"
                  defaultValue={selectedRole?.description || ''}
                  style={{ width: '100%', height: 40.5 }}
                  input={<BootstrapInput />}
                  onChange={({ target: { value } }) => setSelectedRole(value)}
                >
                  {roles.map(opt => (
                    <option value={opt.description} key={opt.id}>
                      {' '}
                      {opt.description}{' '}
                    </option>
                  ))}
                </NativeSelect>
              </div>
            </div>
            <div className="text-right col-sm-2 col-md-2 col-lg-2 col-xl-2 pr-0">
              <button
                className="btn btn-primary font-weight-bold px-2 py-3 mt-1 d-flex justify-content-center"
                style={{ width: '100%' }}
                onClick={addPermission}
                disabled={!selectedRole || !selectedProject}
              >
                <span className="svg-icon svg-icon-sm mr-2">
                  <SVG
                    uniquifyIDs
                    cacheRequests
                    src={toAbsoluteUrl('/media/svg/senseworks/add.svg')}
                  />
                </span>
                <span style={{ marginTop: 2 }}>
                  {' '}
                  {props.intl.formatMessage({ id: 'ADD' })}{' '}
                </span>
              </button>
            </div>
          </div>
          <Table className="mt-3">
            <Head>
              <Cell align="left" width={'50%'}>
                {props.intl.formatMessage({ id: 'PROJECT' })}
              </Cell>
              <Cell align="left" width={'40%'}>
                {props.intl.formatMessage({ id: 'ROLE' })}
              </Cell>
              <Cell align="center" width={'10%'}></Cell>
            </Head>
            <Body>
              {formik.values.roles.map((per, index) => (
                <Row key={index}>
                  <Cell align="left">
                    {projects.find(proj => proj.id === +per.projectId).name}
                  </Cell>
                  <Cell align="left">{per.role}</Cell>
                  <Cell align="center">
                    <DeleteButton
                      onClick={() => removePermission(per.projectId)}
                    />
                  </Cell>
                </Row>
              ))}
            </Body>
          </Table>
          <ErrorMessage field="roles" formik={formik} />
        </div>
        {picture.file ? (
          <ImageCrop
            open={true}
            picture={picture}
            confirmCrop={file => {
              formik.setFieldValue('picture', file);
              setPicture({ fileUrl: '', file: '', filename: '' });
            }}
            onClose={() => {
              setPicture({ fileUrl: '', file: '', filename: '' });
            }}
          />
        ) : null}
      </div>
    </div>
  );
}
export default injectIntl(
  connect(null, auth.actions)(BusinessEntitiesUsersCreate)
);
